import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ExternalLink,
  LogOut,
  Users,
  FileText,
  Briefcase,
  Video,
  Building2,
  MessageCircle,
  Trash2,
  Plus,
  Edit,
  Stethoscope,
  BarChart3,
  Image,
  Settings,
} from "lucide-react";
import axios from "axios";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import LeadsManagement from "@/components/LeadsManagement";
import ImageUpload from "@/components/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_medical-compass-4/artifacts/d093vnr0_Paxgrow%20Final%20Logo.png";
const WA = "917289007800";

function getAuth() {
  const token = localStorage.getItem("admin_token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

function formatWA(lead) {
  const msg = `*Lead: ${lead.name}*\nEmail: ${lead.email}\nPhone: ${lead.phone}\nType: ${lead.lead_type}\n${lead.treatment ? `Treatment: ${lead.treatment}` : ""}${lead.subject ? `Subject: ${lead.subject}` : ""}`;
  return `https://wa.me/${WA}?text=${encodeURIComponent(msg)}`;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [videos, setVideos] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [showHospitalForm, setShowHospitalForm] = useState(false);
  const [showTreatmentForm, setShowTreatmentForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingHospital, setEditingHospital] = useState(null);
  const [editingTreatment, setEditingTreatment] = useState(null);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const navigate = useNavigate();

  // Form states
  const [videoForm, setVideoForm] = useState({
    name: "",
    country: "",
    treatment: "",
    video_type: "youtube",
    video_url: "",
    description: "",
    featured: false,
  });
  const [partnerForm, setPartnerForm] = useState({
    name: "",
    logo_url: "",
    website_url: "",
    description: "",
    order: 0,
    active: true,
    image_input_disabled: false,
  });
  const [hospitalForm, setHospitalForm] = useState({
    name: "",
    city: "",
    description: "",
    beds: "",
    accreditation: "",
    specialties: "",
    logo_url: "",
    image_input_disabled: false,
  });
  const [treatmentForm, setTreatmentForm] = useState({
    name: "",
    description: "",
    price_range: "",
    icon: "Heart",
    image_url: "",
    image_input_disabled: false,
  });
  const [blogForm, setBlogForm] = useState({
    title: "",
    slug: "",
    content: "",
    excerpt: "",
    image: "",
    author: "Paxgrow Team",
    category: "Health",
    tags: "",
    published: true,
    image_input_disabled: false,
  });

  const loadData = useCallback(async () => {
    try {
      const [s, l, b, t, h, v, p] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuth()),
        axios.get(`${API}/admin/leads?limit=50`, getAuth()),
        axios.get(`${API}/blogs?limit=20`),
        axios.get(`${API}/treatments`),
        axios.get(`${API}/hospitals`),
        axios.get(`${API}/videos`),
        axios.get(`${API}/partners`),
      ]);
      setStats(s.data);
      setLeads(l.data.leads);
      setBlogs(b.data.blogs);
      setTreatments(t.data);
      setHospitals(h.data);
      setVideos(v.data);
      setPartners(p.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    loadData();
  }, [loadData, navigate]);

  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this blog?")) return;
    await axios.delete(`${API}/admin/blogs/${id}`, getAuth());
    setBlogs(blogs.filter((b) => b.id !== id && b.slug !== id));
  };

  // Blog CRUD
  const saveBlog = async () => {
    try {
      if (editingBlog) {
        await axios.put(
          `${API}/admin/blogs/${editingBlog.id || editingBlog.slug}`,
          blogForm,
          getAuth(),
        );
      } else {
        await axios.post(`${API}/admin/blogs`, blogForm, getAuth());
      }
      setShowBlogForm(false);
      setEditingBlog(null);
      setBlogForm({
        title: "",
        slug: "",
        content: "",
        excerpt: "",
        image: "",
        author: "Paxgrow Team",
        category: "Health",
        tags: "",
        published: true,
      });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || "Error saving blog");
    }
  };

  const editBlog = (blog) => {
    setBlogForm({
      title: blog.title || "",
      slug: blog.slug || "",
      content: blog.content || "",
      excerpt: blog.excerpt || "",
      image: blog.image || blog.featured_image || "",
      author: blog.author || "Paxgrow Team",
      category: blog.category || "Health",
      tags: blog.tags || "",
      published: blog.published !== false,
    });
    setEditingBlog(blog);
    setShowBlogForm(true);
  };

  // Image Upload Handler
  const handleImageUpload = async (file, category, callback) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", category);
      const res = await axios.post(`${API}/admin/upload/image`, formData, {
        ...getAuth(),
        headers: {
          ...getAuth().headers,
          "Content-Type": "multipart/form-data",
        },
      });
      callback(res.data.url);
    } catch (err) {
      alert(err.response?.data?.detail || "Error uploading image");
    } finally {
      setUploadingImage(false);
    }
  };

  const updateTreatment = async (id, field, value) => {
    await axios.put(
      `${API}/admin/treatments/${id}`,
      { [field]: value },
      getAuth(),
    );
    loadData();
  };

  // Hospital CRUD
  const saveHospital = async () => {
    try {
      const data = {
        ...hospitalForm,
        specialties: hospitalForm.specialties
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (editingHospital) {
        await axios.put(
          `${API}/admin/hospitals/${editingHospital.id}`,
          data,
          getAuth(),
        );
      } else {
        await axios.post(`${API}/admin/hospitals`, data, getAuth());
      }
      setShowHospitalForm(false);
      setEditingHospital(null);
      setHospitalForm({
        name: "",
        city: "",
        description: "",
        beds: "",
        accreditation: "",
        specialties: "",
        logo_url: "",
      });
      loadData();
    } catch (err) {
      alert("Error saving hospital");
    }
  };

  const deleteHospital = async (id) => {
    if (!window.confirm("Delete this hospital?")) return;
    await axios.delete(`${API}/admin/hospitals/${id}`, getAuth());
    loadData();
  };

  const editHospital = (hospital) => {
    setHospitalForm({
      ...hospital,
      specialties: hospital.specialties?.join(", ") || "",
      logo_url: hospital.logo_url || "",
    });
    setEditingHospital(hospital);
    setShowHospitalForm(true);
  };

  // Treatment CRUD
  const saveTreatment = async () => {
    try {
      if (editingTreatment) {
        await axios.put(
          `${API}/admin/treatments/${editingTreatment.id}`,
          treatmentForm,
          getAuth(),
        );
      } else {
        await axios.post(`${API}/admin/treatments`, treatmentForm, getAuth());
      }
      setShowTreatmentForm(false);
      setEditingTreatment(null);
      setTreatmentForm({
        name: "",
        description: "",
        price_range: "",
        icon: "Heart",
        image_url: "",
      });
      loadData();
    } catch (err) {
      alert("Error saving treatment");
    }
  };

  const deleteTreatment = async (id) => {
    if (!window.confirm("Delete this treatment?")) return;
    await axios.delete(`${API}/admin/treatments/${id}`, getAuth());
    loadData();
  };

  const editTreatment = (treatment) => {
    setTreatmentForm({
      ...treatment,
      image_url: treatment.image_url || "",
    });
    setEditingTreatment(treatment);
    setShowTreatmentForm(true);
  };

  // Video CRUD
  const saveVideo = async () => {
    try {
      if (editingVideo) {
        await axios.put(
          `${API}/admin/videos/${editingVideo.id}`,
          videoForm,
          getAuth(),
        );
      } else {
        await axios.post(`${API}/admin/videos`, videoForm, getAuth());
      }
      setShowVideoForm(false);
      setEditingVideo(null);
      setVideoForm({
        name: "",
        country: "",
        treatment: "",
        video_type: "youtube",
        video_url: "",
        description: "",
        featured: false,
      });
      loadData();
    } catch (err) {
      alert("Error saving video");
    }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await axios.delete(`${API}/admin/videos/${id}`, getAuth());
    loadData();
  };

  const editVideo = (video) => {
    setVideoForm(video);
    setEditingVideo(video);
    setShowVideoForm(true);
  };

  // Partner CRUD
  const savePartner = async () => {
    try {
      if (editingPartner) {
        await axios.put(
          `${API}/admin/partners/${editingPartner.id}`,
          partnerForm,
          getAuth(),
        );
      } else {
        await axios.post(`${API}/admin/partners`, partnerForm, getAuth());
      }
      setShowPartnerForm(false);
      setEditingPartner(null);
      setPartnerForm({
        name: "",
        logo_url: "",
        website_url: "",
        description: "",
        order: 0,
        active: true,
      });
      loadData();
    } catch (err) {
      alert("Error saving partner");
    }
  };

  const deletePartner = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    await axios.delete(`${API}/admin/partners/${id}`, getAuth());
    loadData();
  };

  const editPartner = (partner) => {
    setPartnerForm(partner);
    setEditingPartner(partner);
    setShowPartnerForm(true);
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loading-bar" />
      </div>
    );

  const handleUploadSuccess = (url, fileId) => {
    console.log("Image URL:", url);
    console.log("File ID:", fileId);

    setHospitalForm({
      ...hospitalForm,
      logo_url: url,
      image_input_disabled: true,
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <nav className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Paxgrow" className="h-8" />
          <span className="font-heading text-lg font-semibold text-slate-900">
            Admin CMS
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          data-testid="admin-logout"
        >
          <LogOut className="h-4 w-4 mr-1" /> Logout
        </Button>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { icon: Users, label: "Consultations", value: stats.consultations },
            { icon: MessageCircle, label: "Contacts", value: stats.contacts },
            { icon: Briefcase, label: "Career Apps", value: stats.careers },
            { icon: FileText, label: "Blog Posts", value: stats.blogs },
            { icon: Video, label: "Videos", value: stats.videos },
            { icon: Building2, label: "Partners", value: stats.partners },
            { icon: Building2, label: "Hospitals", value: stats.hospitals },
            { icon: Stethoscope, label: "Treatments", value: stats.treatments },
          ].map((s, i) => (
            <Card
              key={i}
              className="bg-white rounded-xl p-4 border border-slate-100"
              data-testid={`admin-stat-${i}`}
            >
              <s.icon className="h-5 w-5 text-orange-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">
                {s.value || 0}
              </p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="analytics">
          <TabsList className="bg-white border border-slate-200 rounded-lg mb-6 flex-wrap">
            <TabsTrigger value="analytics">
              <BarChart3 className="h-4 w-4 mr-1" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="leads">
              Leads ({stats.total_leads || 0})
            </TabsTrigger>
            <TabsTrigger value="blogs">Blogs</TabsTrigger>
            <TabsTrigger value="hospitals">Hospitals</TabsTrigger>
            <TabsTrigger value="treatments">Treatments</TabsTrigger>
            <TabsTrigger value="doctors">
              <Stethoscope className="h-4 w-4 mr-1" /> Doctors
            </TabsTrigger>
            <TabsTrigger value="videos">Videos</TabsTrigger>
            <TabsTrigger value="partners">Partners</TabsTrigger>
            <TabsTrigger value="hero">
              <Image className="h-4 w-4 mr-1" /> Hero Slides
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-1" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          <TabsContent value="leads">
            <LeadsManagement />
          </TabsContent>

          <TabsContent value="blogs">
            <div className="mb-4">
              <Button
                onClick={() => {
                  setShowBlogForm(true);
                  setEditingBlog(null);
                  setBlogForm({
                    title: "",
                    slug: "",
                    content: "",
                    excerpt: "",
                    image: "",
                    author: "Paxgrow Team",
                    category: "Health",
                    tags: "",
                    published: true,
                  });
                }}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="add-blog-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Blog
              </Button>
            </div>

            {showBlogForm && (
              <Card className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
                <h3 className="font-semibold text-slate-900 mb-4">
                  {editingBlog ? "Edit" : "Add"} Blog Post
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Title *</Label>
                    <Input
                      value={blogForm.title}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, title: e.target.value })
                      }
                      placeholder="Blog Title"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Slug *</Label>
                    <Input
                      value={blogForm.slug}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, slug: e.target.value })
                      }
                      placeholder="blog-url-slug"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Category</Label>
                    <Select
                      value={blogForm.category}
                      onValueChange={(v) =>
                        setBlogForm({ ...blogForm, category: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Health">Health</SelectItem>
                        <SelectItem value="Treatment">Treatment</SelectItem>
                        <SelectItem value="Wellness">Wellness</SelectItem>
                        <SelectItem value="Medical Tourism">
                          Medical Tourism
                        </SelectItem>
                        <SelectItem value="Success Stories">
                          Success Stories
                        </SelectItem>
                        <SelectItem value="Tips">Tips</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Author</Label>
                    <Input
                      value={blogForm.author}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, author: e.target.value })
                      }
                      placeholder="Author Name"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">Excerpt</Label>
                    <Textarea
                      value={blogForm.excerpt}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, excerpt: e.target.value })
                      }
                      rows={2}
                      placeholder="Short description..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">Content *</Label>
                    <Textarea
                      value={blogForm.content}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, content: e.target.value })
                      }
                      rows={6}
                      placeholder="Blog content (supports HTML)..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      Featured Image
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        disabled={blogForm.image_input_disabled || false}
                        value={blogForm.image}
                        onChange={(e) =>
                          setBlogForm({ ...blogForm, image: e.target.value })
                        }
                        placeholder="Image URL or upload"
                        className="flex-1"
                      />
                      <label className="cursor-pointer">
                        {/* <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={(e) => handleImageUpload(e.target.files?.[0], "blog", (url) => setBlogForm({...blogForm, image: url}))}
                        /> */}
                        {/* <Button type="button" variant="outline" size="sm" disabled={uploadingImage}>
                          {uploadingImage ? "..." : <Image className="h-4 w-4" />}
                        </Button> */}
                      </label>
                      <ImageUpload
                        onUploadSuccess={(url, fileId) =>
                          setBlogForm({
                            ...blogForm,
                            image: url,
                            image_input_disabled: true,
                          })
                        }
                      />
                    </div>
                    {blogForm.image && (
                      <img
                        src={blogForm.image}
                        alt="Preview"
                        className="mt-2 h-20 rounded object-cover"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      Tags (comma separated)
                    </Label>
                    <Input
                      value={blogForm.tags}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, tags: e.target.value })
                      }
                      placeholder="health, surgery, tips"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={blogForm.published}
                      onChange={(e) =>
                        setBlogForm({
                          ...blogForm,
                          published: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    <Label className="text-sm text-slate-600">Published</Label>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={saveBlog}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Save Blog
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowBlogForm(false);
                      setEditingBlog(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            )}

            <Card className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-500">
                        Title
                      </th>
                      <th className="text-left p-3 font-medium text-slate-500">
                        Category
                      </th>
                      <th className="text-left p-3 font-medium text-slate-500">
                        Date
                      </th>
                      <th className="text-left p-3 font-medium text-slate-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.map((b, i) => (
                      <tr
                        key={i}
                        className="border-b border-slate-50 hover:bg-slate-50"
                      >
                        <td className="p-3 font-medium text-slate-900 max-w-md truncate">
                          {b.title}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">
                            {b.category}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs text-slate-400">{b.date}</td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => editBlog(b)}
                              className="text-slate-500 hover:text-slate-700"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteBlog(b.id || b.slug)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="hospitals">
            <div className="mb-4">
              <Button
                onClick={() => {
                  setShowHospitalForm(true);
                  setEditingHospital(null);
                  setHospitalForm({
                    name: "",
                    city: "",
                    description: "",
                    beds: "",
                    accreditation: "",
                    specialties: "",
                  });
                }}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="add-hospital-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Hospital
              </Button>
            </div>

            {showHospitalForm && (
              <Card className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
                <h3 className="font-semibold text-slate-900 mb-4">
                  {editingHospital ? "Edit" : "Add"} Hospital
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">
                      Hospital Name *
                    </Label>
                    <Input
                      value={hospitalForm.name}
                      onChange={(e) =>
                        setHospitalForm({
                          ...hospitalForm,
                          name: e.target.value,
                        })
                      }
                      placeholder="e.g., Apollo Hospital"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">City *</Label>
                    <Input
                      value={hospitalForm.city}
                      onChange={(e) =>
                        setHospitalForm({
                          ...hospitalForm,
                          city: e.target.value,
                        })
                      }
                      placeholder="e.g., Delhi"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Beds</Label>
                    <Input
                      value={hospitalForm.beds}
                      onChange={(e) =>
                        setHospitalForm({
                          ...hospitalForm,
                          beds: e.target.value,
                        })
                      }
                      placeholder="e.g., 500"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      Accreditation
                    </Label>
                    <Input
                      value={hospitalForm.accreditation}
                      onChange={(e) =>
                        setHospitalForm({
                          ...hospitalForm,
                          accreditation: e.target.value,
                        })
                      }
                      placeholder="e.g., NABH, JCI"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">
                      Hospital Logo/Image
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={hospitalForm.logo_url}
                        onChange={(e) =>
                          setHospitalForm({
                            ...hospitalForm,
                            logo_url: e.target.value,
                          })
                        }
                        placeholder="Image URL or upload"
                        className="flex-1"
                      />
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          // onChange={(e) =>
                          //   handleImageUpload(
                          //     e.target.files?.[0],
                          //     "hospital",
                          //     (url) =>
                          //       setHospitalForm({
                          //         ...hospitalForm,
                          //         logo_url: url,
                          //       }),
                          //   )
                          // }
                        />
                        {/* <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? (
                            "..."
                          ) : (
                            <Image className="h-4 w-4" />
                          )}
                        </Button> */}
                      </label>
                      <ImageUpload
                        onUploadSuccess={(url, fileId) =>
                          setHospitalForm({
                            ...hospitalForm,
                            logo_url: url,
                            image_input_disabled: true,
                          })
                        }
                      />
                    </div>
                    {hospitalForm.logo_url && (
                      <img
                        src={hospitalForm.logo_url}
                        alt="Preview"
                        className="mt-2 h-16 rounded object-contain"
                      />
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">
                      Specialties (comma separated)
                    </Label>
                    <Input
                      value={hospitalForm.specialties}
                      onChange={(e) =>
                        setHospitalForm({
                          ...hospitalForm,
                          specialties: e.target.value,
                        })
                      }
                      placeholder="e.g., Cardiology, Orthopedics, Oncology"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">
                      Description
                    </Label>
                    <Textarea
                      value={hospitalForm.description}
                      onChange={(e) =>
                        setHospitalForm({
                          ...hospitalForm,
                          description: e.target.value,
                        })
                      }
                      rows={2}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={saveHospital}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Save Hospital
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowHospitalForm(false);
                      setEditingHospital(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {hospitals.map((h) => (
                <Card
                  key={h.id}
                  className="bg-white rounded-xl border border-slate-100 p-4"
                  data-testid={`admin-hospital-${h.id}`}
                >
                  <h4 className="font-semibold text-slate-900 text-sm">
                    {h.name}
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">
                    {h.city} | {h.beds} beds | {h.accreditation}
                  </p>
                  <p className="text-xs text-slate-600 mb-3 line-clamp-2">
                    {h.description}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => editHospital(h)}
                    >
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => deleteHospital(h.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="treatments">
            <div className="mb-4">
              <Button
                onClick={() => {
                  setShowTreatmentForm(true);
                  setEditingTreatment(null);
                  setTreatmentForm({
                    name: "",
                    description: "",
                    price_range: "",
                    icon: "Heart",
                  });
                }}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="add-treatment-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Treatment
              </Button>
            </div>

            {showTreatmentForm && (
              <Card className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
                <h3 className="font-semibold text-slate-900 mb-4">
                  {editingTreatment ? "Edit" : "Add"} Treatment
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">
                      Treatment Name *
                    </Label>
                    <Input
                      value={treatmentForm.name}
                      onChange={(e) =>
                        setTreatmentForm({
                          ...treatmentForm,
                          name: e.target.value,
                        })
                      }
                      placeholder="e.g., Heart Bypass Surgery"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      Price Range
                    </Label>
                    <Input
                      value={treatmentForm.price_range}
                      onChange={(e) =>
                        setTreatmentForm({
                          ...treatmentForm,
                          price_range: e.target.value,
                        })
                      }
                      placeholder="e.g., $5,000 - $10,000"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">
                      Treatment Image
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        disabled={treatmentForm.image_input_disabled || false}
                        value={treatmentForm.image_url}
                        onChange={(e) =>
                          setTreatmentForm({
                            ...treatmentForm,
                            image_url: e.target.value,
                          })
                        }
                        placeholder="Image URL or upload"
                        className="flex-1"
                      />
                      <label className="cursor-pointer">
                        <ImageUpload
                          onUploadSuccess={(url, fileId) =>
                            setTreatmentForm({
                              ...treatmentForm,
                              image_url: url,
                              image_input_disabled: true,
                            })
                          }
                        />
                      </label>
                    </div>
                    {treatmentForm.image_url && (
                      <img
                        src={treatmentForm.image_url}
                        alt="Preview"
                        className="mt-2 h-16 rounded object-cover"
                      />
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">
                      Description
                    </Label>
                    <Textarea
                      value={treatmentForm.description}
                      onChange={(e) =>
                        setTreatmentForm({
                          ...treatmentForm,
                          description: e.target.value,
                        })
                      }
                      rows={2}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={saveTreatment}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Save Treatment
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowTreatmentForm(false);
                      setEditingTreatment(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {treatments.map((t) => (
                <Card
                  key={t.id}
                  className="bg-white rounded-xl border border-slate-100 p-4"
                  data-testid={`admin-treatment-${t.id}`}
                >
                  <h4 className="font-semibold text-slate-900 text-sm">
                    {t.name}
                  </h4>
                  <p className="text-xs text-orange-600 mb-2">
                    {t.price_range}
                  </p>
                  <p className="text-xs text-slate-600 mb-3 line-clamp-2">
                    {t.description}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => editTreatment(t)}
                    >
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => deleteTreatment(t.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="videos">
            <div className="mb-4">
              <Button
                onClick={() => {
                  setShowVideoForm(true);
                  setEditingVideo(null);
                  setVideoForm({
                    name: "",
                    country: "",
                    treatment: "",
                    video_type: "youtube",
                    video_url: "",
                    description: "",
                    featured: false,
                  });
                }}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="add-video-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Video Testimonial
              </Button>
            </div>

            {showVideoForm && (
              <Card className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
                <h3 className="font-semibold text-slate-900 mb-4">
                  {editingVideo ? "Edit" : "Add"} Video Testimonial
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">
                      Patient Name *
                    </Label>
                    <Input
                      value={videoForm.name}
                      onChange={(e) =>
                        setVideoForm({ ...videoForm, name: e.target.value })
                      }
                      placeholder="e.g., John Doe"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Country *</Label>
                    <Input
                      value={videoForm.country}
                      onChange={(e) =>
                        setVideoForm({ ...videoForm, country: e.target.value })
                      }
                      placeholder="e.g., Nigeria"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      Treatment *
                    </Label>
                    <Input
                      value={videoForm.treatment}
                      onChange={(e) =>
                        setVideoForm({
                          ...videoForm,
                          treatment: e.target.value,
                        })
                      }
                      placeholder="e.g., Knee Replacement"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      Video Type *
                    </Label>
                    <Select
                      value={videoForm.video_type}
                      onValueChange={(v) =>
                        setVideoForm({ ...videoForm, video_type: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="upload">Direct Upload</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">
                      Video URL *
                    </Label>
                    <Input
                      value={videoForm.video_url}
                      onChange={(e) =>
                        setVideoForm({
                          ...videoForm,
                          video_url: e.target.value,
                        })
                      }
                      placeholder={
                        videoForm.video_type === "youtube"
                          ? "YouTube URL"
                          : "Video file URL"
                      }
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">
                      Description
                    </Label>
                    <Textarea
                      value={videoForm.description}
                      onChange={(e) =>
                        setVideoForm({
                          ...videoForm,
                          description: e.target.value,
                        })
                      }
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={videoForm.featured}
                      onChange={(e) =>
                        setVideoForm({
                          ...videoForm,
                          featured: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    <Label className="text-sm text-slate-600">
                      Featured video
                    </Label>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={saveVideo}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Save Video
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowVideoForm(false);
                      setEditingVideo(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((v) => (
                <Card
                  key={v.id}
                  className="bg-white rounded-xl border border-slate-100 p-4"
                  data-testid={`admin-video-${v.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold text-slate-900">{v.name}</h4>
                      <p className="text-xs text-slate-500">
                        {v.country} - {v.treatment}
                      </p>
                    </div>
                    {v.featured && (
                      <Badge className="bg-orange-100 text-orange-700 text-xs">
                        Featured
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                    {v.description}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => editVideo(v)}
                    >
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => deleteVideo(v.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="partners">
            <div className="mb-4">
              <Button
                onClick={() => {
                  setShowPartnerForm(true);
                  setEditingPartner(null);
                  setPartnerForm({
                    name: "",
                    logo_url: "",
                    website_url: "",
                    description: "",
                    order: 0,
                    active: true,
                  });
                }}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="add-partner-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Partner
              </Button>
            </div>

            {showPartnerForm && (
              <Card className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
                <h3 className="font-semibold text-slate-900 mb-4">
                  {editingPartner ? "Edit" : "Add"} Partner
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">
                      Partner Name *
                    </Label>
                    <Input
                      value={partnerForm.name}
                      onChange={(e) =>
                        setPartnerForm({ ...partnerForm, name: e.target.value })
                      }
                      placeholder="e.g., Apollo Hospitals"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Logo URL *</Label>
                    <div className="flex gap-2">
                      <Input
                        disabled={partnerForm.image_input_disabled || false}
                        value={partnerForm.logo_url}
                        onChange={(e) =>
                          setPartnerForm({
                            ...partnerForm,
                            logo_url: e.target.value,
                          })
                        }
                        placeholder="https://..."
                      />
                      <label className="cursor-pointer">
                        <ImageUpload
                          onUploadSuccess={(url, fileId) =>
                            setPartnerForm({
                              ...partnerForm,
                              logo_url: url,
                              image_input_disabled: true,
                            })
                          }
                        />
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      Website URL
                    </Label>
                    <Input
                      value={partnerForm.website_url}
                      onChange={(e) =>
                        setPartnerForm({
                          ...partnerForm,
                          website_url: e.target.value,
                        })
                      }
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      Display Order
                    </Label>
                    <Input
                      type="number"
                      value={partnerForm.order}
                      onChange={(e) =>
                        setPartnerForm({
                          ...partnerForm,
                          order: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">
                      Description
                    </Label>
                    <Textarea
                      value={partnerForm.description}
                      onChange={(e) =>
                        setPartnerForm({
                          ...partnerForm,
                          description: e.target.value,
                        })
                      }
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={partnerForm.active}
                      onChange={(e) =>
                        setPartnerForm({
                          ...partnerForm,
                          active: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    <Label className="text-sm text-slate-600">
                      Active (visible on site)
                    </Label>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={savePartner}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Save Partner
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPartnerForm(false);
                      setEditingPartner(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {partners.map((p) => (
                <Card
                  key={p.id}
                  className="bg-white rounded-xl border border-slate-100 p-4"
                  data-testid={`admin-partner-${p.id}`}
                >
                  <div className="h-20 bg-slate-50 rounded-lg mb-3 flex items-center justify-center p-2">
                    {p.logo_url ? (
                      <img
                        src={p.logo_url}
                        alt={p.name}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <Building2 className="h-8 w-8 text-slate-300" />
                    )}
                  </div>
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-slate-900">{p.name}</h4>
                    <Badge
                      className={
                        p.active
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-500"
                      }
                      variant="outline"
                    >
                      {p.active ? "Active" : "Hidden"}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                    {p.description}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => editPartner(p)}
                    >
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => deletePartner(p.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="hero">
            <HeroSlidesManager />
          </TabsContent>

          <TabsContent value="doctors">
            <DoctorsManager />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Hero Slides Manager Component
function HeroSlidesManager() {
  const [slides, setSlides] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [form, setForm] = useState({
    type: "image",
    media_url: "",
    title: "",
    subtitle: "",
    description: "",
    cta_text: "Get Free Consultation",
    cta_link: "#consultation",
    order: 0,
    active: true,
  });

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      const res = await axios.get(`${API}/hero-slides`);
      setSlides(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "hero");
      const res = await axios.post(`${API}/admin/upload/image`, formData, {
        ...getAuth(),
        headers: {
          ...getAuth().headers,
          "Content-Type": "multipart/form-data",
        },
      });
      setForm({ ...form, media_url: res.data.url });
    } catch (err) {
      alert(err.response?.data?.detail || "Error uploading image");
    } finally {
      setUploadingImage(false);
    }
  };

  const saveSlide = async () => {
    try {
      if (editing) {
        await axios.put(
          `${API}/admin/hero-slides/${editing.id}`,
          form,
          getAuth(),
        );
      } else {
        await axios.post(`${API}/admin/hero-slides`, form, getAuth());
      }
      setShowForm(false);
      setEditing(null);
      setForm({
        type: "image",
        media_url: "",
        title: "",
        subtitle: "",
        description: "",
        cta_text: "Get Free Consultation",
        cta_link: "#consultation",
        order: 0,
        active: true,
      });
      loadSlides();
    } catch (err) {
      alert("Error saving slide");
    }
  };

  const deleteSlide = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    await axios.delete(`${API}/admin/hero-slides/${id}`, getAuth());
    loadSlides();
  };

  const editSlide = (slide) => {
    setForm(slide);
    setEditing(slide);
    setShowForm(true);
  };

  return (
    <div data-testid="hero-slides-manager">
      <div className="mb-4">
        <Button
          onClick={() => {
            setShowForm(true);
            setEditing(null);
            setForm({
              type: "image",
              media_url: "",
              title: "",
              subtitle: "",
              description: "",
              cta_text: "Get Free Consultation",
              cta_link: "#consultation",
              order: 0,
              active: true,
            });
          }}
          className="bg-orange-600 hover:bg-orange-700"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Hero Slide
        </Button>
      </div>

      {showForm && (
        <Card className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
          <h3 className="font-semibold text-slate-900 mb-4">
            {editing ? "Edit" : "Add"} Hero Slide
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Media Type *</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Order</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) =>
                  setForm({ ...form, order: parseInt(e.target.value) || 0 })
                }
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Media URL *</Label>
              <div className="flex gap-2">
                <Input
                  value={form.media_url}
                  onChange={(e) =>
                    setForm({ ...form, media_url: e.target.value })
                  }
                  placeholder="Image/Video URL or upload"
                  className="flex-1"
                />
                {form.type === "image" && (
                  <label className="cursor-pointer">
                    <ImageUpload
                      onUploadSuccess={(url, fileId) =>
                        setForm({
                          ...form,
                          media_url: url,
                          // image_input_disabled: true,
                        })
                      }
                    />
                  </label>
                )}
              </div>
              {form.media_url && form.type === "image" && (
                <img
                  src={form.media_url}
                  alt="Preview"
                  className="mt-2 h-20 rounded object-cover"
                />
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="World-Class Healthcare"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Subtitle</Label>
              <Input
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                placeholder="Made Affordable"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                rows={2}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">CTA Text</Label>
              <Input
                value={form.cta_text}
                onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">CTA Link</Label>
              <Input
                value={form.cta_link}
                onChange={(e) => setForm({ ...form, cta_link: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="rounded"
              />
              <Label className="text-sm text-slate-600">
                Active (visible on homepage)
              </Label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              onClick={saveSlide}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Save Slide
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowForm(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {slides.map((s) => (
          <Card
            key={s.id}
            className="bg-white rounded-xl border border-slate-100 overflow-hidden"
            data-testid={`hero-slide-${s.id}`}
          >
            <div className="h-40 bg-slate-100 relative">
              {s.type === "video" ? (
                <video
                  src={s.media_url}
                  className="w-full h-full object-cover"
                  muted
                />
              ) : (
                <img
                  src={s.media_url}
                  alt={s.title}
                  className="w-full h-full object-cover"
                />
              )}
              <Badge className="absolute top-2 right-2 bg-black/50 text-white text-xs">
                {s.type}
              </Badge>
            </div>
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-semibold text-slate-900">{s.title}</h4>
                  <p className="text-xs text-orange-600">{s.subtitle}</p>
                </div>
                <Badge
                  className={
                    s.active
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-100 text-slate-500"
                  }
                >
                  {s.active ? "Active" : "Hidden"}
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mb-3 line-clamp-2">
                {s.description}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => editSlide(s)}
                >
                  <Edit className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-500 hover:text-red-700"
                  onClick={() => deleteSlide(s.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {slides.length === 0 && (
          <div className="col-span-3 p-8 text-center text-slate-500 bg-slate-50 rounded-xl">
            No hero slides yet. Add your first slide to customize the homepage
            hero section.
          </div>
        )}
      </div>
    </div>
  );
}

// Doctors Manager Component
function DoctorsManager() {
  const [doctors, setDoctors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [form, setForm] = useState({
    name: "",
    designation: "",
    specialization: "",
    qualifications: "",
    experience_years: 0,
    hospital: "",
    image_url: "",
    bio: "",
    languages: "English, Hindi",
    consultation_fee: "",
    available_days: "Mon-Sat",
    achievements: "",
    featured: false,
    active: true,
  });

  useEffect(() => {
    loadDoctors();
  }, []);

  const loadDoctors = async () => {
    try {
      const res = await axios.get(`${API}/doctors`);
      setDoctors(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "doctor");
      const res = await axios.post(`${API}/admin/upload/image`, formData, {
        ...getAuth(),
        headers: {
          ...getAuth().headers,
          "Content-Type": "multipart/form-data",
        },
      });
      setForm({ ...form, image_url: res.data.url });
    } catch (err) {
      alert(err.response?.data?.detail || "Error uploading image");
    } finally {
      setUploadingImage(false);
    }
  };

  const saveDoctor = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/doctors/${editing.id}`, form, getAuth());
      } else {
        await axios.post(`${API}/admin/doctors`, form, getAuth());
      }
      setShowForm(false);
      setEditing(null);
      setForm({
        name: "",
        designation: "",
        specialization: "",
        qualifications: "",
        experience_years: 0,
        hospital: "",
        image_url: "",
        bio: "",
        languages: "English, Hindi",
        consultation_fee: "",
        available_days: "Mon-Sat",
        achievements: "",
        featured: false,
        active: true,
      });
      loadDoctors();
    } catch (err) {
      alert("Error saving doctor");
    }
  };

  const deleteDoctor = async (id) => {
    if (!window.confirm("Delete this doctor?")) return;
    await axios.delete(`${API}/admin/doctors/${id}`, getAuth());
    loadDoctors();
  };

  const editDoctor = (doctor) => {
    setForm(doctor);
    setEditing(doctor);
    setShowForm(true);
  };

  return (
    <div data-testid="doctors-manager">
      <div className="mb-4">
        <Button
          onClick={() => {
            setShowForm(true);
            setEditing(null);
            setForm({
              name: "",
              designation: "",
              specialization: "",
              qualifications: "",
              experience_years: 0,
              hospital: "",
              image_url: "",
              bio: "",
              languages: "English, Hindi",
              consultation_fee: "",
              available_days: "Mon-Sat",
              achievements: "",
              featured: false,
              active: true,
            });
          }}
          className="bg-orange-600 hover:bg-orange-700"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Doctor
        </Button>
      </div>

      {showForm && (
        <Card className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
          <h3 className="font-semibold text-slate-900 mb-4">
            {editing ? "Edit" : "Add"} Doctor Profile
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Dr. John Smith"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Designation *</Label>
              <Input
                value={form.designation}
                onChange={(e) =>
                  setForm({ ...form, designation: e.target.value })
                }
                placeholder="Senior Consultant"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Specialization *</Label>
              <Input
                value={form.specialization}
                onChange={(e) =>
                  setForm({ ...form, specialization: e.target.value })
                }
                placeholder="Cardiology"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Qualifications</Label>
              <Input
                value={form.qualifications}
                onChange={(e) =>
                  setForm({ ...form, qualifications: e.target.value })
                }
                placeholder="MBBS, MD, DM"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">
                Experience (Years)
              </Label>
              <Input
                type="number"
                value={form.experience_years}
                onChange={(e) =>
                  setForm({
                    ...form,
                    experience_years: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Hospital</Label>
              <Input
                value={form.hospital}
                onChange={(e) => setForm({ ...form, hospital: e.target.value })}
                placeholder="Medanta Hospital"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Doctor Photo</Label>
              <div className="flex gap-2">
                <Input
                  disabled={form.image_input_disabled || false}
                  value={form.image_url}
                  onChange={(e) =>
                    setForm({ ...form, image_url: e.target.value })
                  }
                  placeholder="Image URL or upload"
                  className="flex-1"
                />
                <label className="cursor-pointer">
                  <ImageUpload
                    onUploadSuccess={(url, fileId) =>
                      setForm({
                        ...form,
                        image_url: url,
                        image_input_disabled: true,
                      })
                    }
                  />
                </label>
              </div>
              {form.image_url && (
                <img
                  src={form.image_url}
                  alt="Preview"
                  className="mt-2 h-20 w-20 rounded-full object-cover"
                />
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Languages</Label>
              <Input
                value={form.languages}
                onChange={(e) =>
                  setForm({ ...form, languages: e.target.value })
                }
                placeholder="English, Hindi"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Consultation Fee</Label>
              <Input
                value={form.consultation_fee}
                onChange={(e) =>
                  setForm({ ...form, consultation_fee: e.target.value })
                }
                placeholder="$50 - $100"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Available Days</Label>
              <Input
                value={form.available_days}
                onChange={(e) =>
                  setForm({ ...form, available_days: e.target.value })
                }
                placeholder="Mon-Sat"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Bio</Label>
              <Textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={2}
                placeholder="Brief biography..."
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Achievements</Label>
              <Textarea
                value={form.achievements}
                onChange={(e) =>
                  setForm({ ...form, achievements: e.target.value })
                }
                rows={2}
                placeholder="Awards, recognitions..."
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) =>
                    setForm({ ...form, featured: e.target.checked })
                  }
                  className="rounded"
                />
                <span className="text-sm text-slate-600">Featured</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) =>
                    setForm({ ...form, active: e.target.checked })
                  }
                  className="rounded"
                />
                <span className="text-sm text-slate-600">Active</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              onClick={saveDoctor}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Save Doctor
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowForm(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {doctors.map((doc) => (
          <Card
            key={doc.id}
            className="bg-white rounded-xl border border-slate-100 overflow-hidden"
            data-testid={`doctor-${doc.id}`}
          >
            <div className="h-40 bg-slate-100 relative">
              {doc.image_url ? (
                <img
                  src={doc.image_url}
                  alt={doc.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Users className="h-12 w-12 text-slate-300" />
                </div>
              )}
              {doc.featured && (
                <Badge className="absolute top-2 left-2 bg-orange-600 text-white text-xs">
                  Featured
                </Badge>
              )}
              <Badge
                className={`absolute top-2 right-2 ${doc.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}
              >
                {doc.active ? "Active" : "Hidden"}
              </Badge>
            </div>
            <div className="p-4">
              <h4 className="font-semibold text-slate-900">{doc.name}</h4>
              <p className="text-xs text-orange-600">{doc.designation}</p>
              <p className="text-xs text-slate-500 mt-1">
                {doc.specialization} | {doc.experience_years} years exp.
              </p>
              <p className="text-xs text-slate-400 mt-1">{doc.hospital}</p>
              <div className="flex gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => editDoctor(doc)}
                >
                  <Edit className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-500 hover:text-red-700"
                  onClick={() => deleteDoctor(doc.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {doctors.length === 0 && (
          <div className="col-span-3 p-8 text-center text-slate-500 bg-slate-50 rounded-xl">
            No doctors yet. Add doctor profiles to showcase your medical
            experts.
          </div>
        )}
      </div>
    </div>
  );
}

// Settings Manager Component (Logo, Social Links)
function SettingsManager() {
  const [logoSettings, setLogoSettings] = useState({
    logo_url: "",
    logo_width: 64,
    logo_height: 64,
    tagline: "Medical Value Travel",
    show_tagline: true,
  });
  const [socialLinks, setSocialLinks] = useState({
    facebook: "",
    instagram: "",
    linkedin: "",
    twitter: "",
    youtube: "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [logoRes, socialRes] = await Promise.all([
        axios.get(`${API}/settings/logo`),
        axios.get(`${API}/settings/social`),
      ]);
      setLogoSettings(logoRes.data);
      setSocialLinks(socialRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const res = await axios.post(`${API}/admin/upload/logo`, formData, {
        ...getAuth(),
        headers: {
          ...getAuth().headers,
          "Content-Type": "multipart/form-data",
        },
      });
      setLogoSettings((prev) => ({ ...prev, logo_url: res.data.url }));
      alert("Logo uploaded successfully!");
    } catch (err) {
      alert(err.response?.data?.detail || "Error uploading logo");
    } finally {
      setUploading(false);
    }
  };

  const saveLogoSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/logo`, logoSettings, getAuth());
      alert("Logo settings saved!");
    } catch (err) {
      alert("Error saving logo settings");
    } finally {
      setSaving(false);
    }
  };

  const saveSocialLinks = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/social`, socialLinks, getAuth());
      alert("Social links saved!");
    } catch (err) {
      alert("Error saving social links");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-manager">
      {/* Logo Settings */}
      <Card className="bg-white rounded-xl border border-slate-100 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Image className="h-5 w-5 text-orange-600" /> Logo Settings
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="mb-4">
              <Label className="text-xs text-slate-500">Current Logo</Label>
              <div className="mt-2 p-4 bg-slate-50 rounded-xl flex items-center justify-center min-h-[120px]">
                {logoSettings.logo_url ? (
                  <img
                    src={logoSettings.logo_url}
                    alt="Current Logo"
                    style={{
                      width: logoSettings.logo_width,
                      height: logoSettings.logo_height,
                    }}
                    className="object-contain"
                  />
                ) : (
                  <p className="text-slate-400 text-sm">No logo uploaded</p>
                )}
              </div>
            </div>

            <div className="mb-4">
              <Label className="text-xs text-slate-500">Upload New Logo</Label>
              <p className="text-xs text-slate-400 mb-2">
                Supports: PNG, JPG, JPEG, SVG, WEBP, GIF, ICO
              </p>
              <Input
                type="file"
                accept=".png,.jpg,.jpeg,.svg,.webp,.gif,.ico"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="cursor-pointer"
              />
              {uploading && (
                <p className="text-xs text-orange-600 mt-1">Uploading...</p>
              )}
            </div>
          </div>

          <div>
            <div className="mb-4">
              <Label className="text-xs text-slate-500">
                Logo URL (or paste external URL)
              </Label>
              <Input
                value={logoSettings.logo_url}
                onChange={(e) =>
                  setLogoSettings({ ...logoSettings, logo_url: e.target.value })
                }
                placeholder="https://..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-xs text-slate-500">Width (px)</Label>
                <Input
                  type="number"
                  value={logoSettings.logo_width}
                  onChange={(e) =>
                    setLogoSettings({
                      ...logoSettings,
                      logo_width: parseInt(e.target.value) || 64,
                    })
                  }
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Height (px)</Label>
                <Input
                  type="number"
                  value={logoSettings.logo_height}
                  onChange={(e) =>
                    setLogoSettings({
                      ...logoSettings,
                      logo_height: parseInt(e.target.value) || 64,
                    })
                  }
                />
              </div>
            </div>

            <div className="mb-4">
              <Label className="text-xs text-slate-500">Tagline</Label>
              <Input
                value={logoSettings.tagline}
                onChange={(e) =>
                  setLogoSettings({ ...logoSettings, tagline: e.target.value })
                }
                placeholder="Medical Value Travel"
              />
            </div>

            <label className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                checked={logoSettings.show_tagline}
                onChange={(e) =>
                  setLogoSettings({
                    ...logoSettings,
                    show_tagline: e.target.checked,
                  })
                }
                className="rounded"
              />
              <span className="text-sm text-slate-600">
                Show tagline below logo
              </span>
            </label>

            <Button
              onClick={saveLogoSettings}
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {saving ? "Saving..." : "Save Logo Settings"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Social Links */}
      <Card className="bg-white rounded-xl border border-slate-100 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-orange-600" /> Social Media Links
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-500 flex items-center gap-2">
              <svg
                className="h-4 w-4 text-blue-600"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              Facebook
            </Label>
            <Input
              value={socialLinks.facebook}
              onChange={(e) =>
                setSocialLinks({ ...socialLinks, facebook: e.target.value })
              }
              placeholder="https://facebook.com/paxgrow"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500 flex items-center gap-2">
              <svg
                className="h-4 w-4 text-pink-600"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
              Instagram
            </Label>
            <Input
              value={socialLinks.instagram}
              onChange={(e) =>
                setSocialLinks({ ...socialLinks, instagram: e.target.value })
              }
              placeholder="https://instagram.com/paxgrow"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500 flex items-center gap-2">
              <svg
                className="h-4 w-4 text-blue-700"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              LinkedIn
            </Label>
            <Input
              value={socialLinks.linkedin}
              onChange={(e) =>
                setSocialLinks({ ...socialLinks, linkedin: e.target.value })
              }
              placeholder="https://linkedin.com/company/paxgrow"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500 flex items-center gap-2">
              <svg
                className="h-4 w-4 text-black"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              X (Twitter)
            </Label>
            <Input
              value={socialLinks.twitter}
              onChange={(e) =>
                setSocialLinks({ ...socialLinks, twitter: e.target.value })
              }
              placeholder="https://twitter.com/paxgrow"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500 flex items-center gap-2">
              <svg
                className="h-4 w-4 text-red-600"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              YouTube
            </Label>
            <Input
              value={socialLinks.youtube}
              onChange={(e) =>
                setSocialLinks({ ...socialLinks, youtube: e.target.value })
              }
              placeholder="https://youtube.com/@paxgrow"
            />
          </div>
        </div>

        <div className="mt-4">
          <Button
            onClick={saveSocialLinks}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {saving ? "Saving..." : "Save Social Links"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
